import React, { useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Polygon } from "react-leaflet";

import { SENTIMENT_COLORS, SENTIMENT_LABELS } from "../lib/format";
import { SDNP_APPROX_POLYGON } from "../lib/sdnp_boundary";

const DEFAULT_CENTRE = [50.94, -0.85];
const DEFAULT_ZOOM = 9;

const VIEW_INTERPRETIVE = "interpretive";
const VIEW_SUPPORT = "support";

// Four-band "Support for onshore wind" classification.
// Strips landscape/planning constraint dampening so we see the technology
// signal directly, but adds an amber band for workshops that effectively
// ruled out the discussion early without engaging in design.
const SUPPORT_COLORS = {
  strong: "#047857",      // deep emerald — strongly in favour, very active
  broad: "#10B981",       // emerald — broadly supportive (sits clearly between dark and amber)
  ruled_out: "#D97706",   // amber — effectively ruled out, minimal engagement
  against: "#DC2626",     // red — actually opposed to the technology
  unknown: "#94A3B8",
};
const SUPPORT_LABELS = {
  strong: "Strongly in favour",
  broad: "Broadly supportive",
  ruled_out: "Effectively ruled out",
  against: "Against",
  unknown: "Not classified",
};

// SME overrides — workshops where the heuristic doesn't capture the
// nuance of the report. Keyed by parish_or_town. Add or remove entries
// here as the dataset is reviewed by hand.
const MANUAL_RULED_OUT_PARISHES = new Set([
  "Ringwood",
]);
const MANUAL_BROADLY_SUPPORTIVE_PARISHES = new Set([
  "Liss",
]);

function supportBand(workshop) {
  const s = workshop.sentiment_interpretive || workshop.sentiment;

  // Red — genuine rejection of onshore wind itself
  if (s === "resistant") return "against";

  // SME overrides — explicit human classification trumps the heuristic
  if (MANUAL_RULED_OUT_PARISHES.has(workshop.parish_or_town)) {
    return "ruled_out";
  }
  if (MANUAL_BROADLY_SUPPORTIVE_PARISHES.has(workshop.parish_or_town)) {
    return "broad";
  }

  // Dark green — the LLM only assigns strongly_supportive when participants
  // engaged actively (proposed turbines, optimised siting, positive language).
  if (s === "strongly_supportive") return "strong";

  // Engagement signal — did they propose a concrete turbine count?
  const tcRaw = workshop.turbine_count_proposed;
  const hasTurbineCount = !!(
    tcRaw &&
    String(tcRaw).trim() &&
    String(tcRaw).trim() !== "0"
  );

  // Amber — technically not opposed, but did not engage with the design
  // discussion: no concrete turbine proposal landed in the workshop.
  if (s === "constrained") return "ruled_out";
  if (s === "supportive_but_constrained" && !hasTurbineCount) {
    return "ruled_out";
  }

  // Light green — broad support; engaged constructively with the topic.
  return "broad";
}

function pinAppearance(workshop, view) {
  if (view === VIEW_SUPPORT) {
    const band = supportBand(workshop);
    return {
      color: SUPPORT_COLORS[band] || SUPPORT_COLORS.unknown,
      label: SUPPORT_LABELS[band],
    };
  }
  const s = workshop.sentiment;
  return {
    color: SENTIMENT_COLORS[s] || "#64748B",
    label: SENTIMENT_LABELS[s] || "Not classified",
  };
}

export default function MapTab({ workshops, onSelect }) {
  const [view, setView] = useState(VIEW_INTERPRETIVE);

  const pinned = useMemo(
    () =>
      workshops.filter(
        (w) => typeof w.lat === "number" && typeof w.lon === "number"
      ),
    [workshops]
  );
  const missing = workshops.length - pinned.length;

  const titleCopy =
    view === VIEW_SUPPORT
      ? {
          h: "Support for onshore wind",
          p: "Each pin is a workshop, coloured by their underlying support for onshore wind as a technology — independent of landscape, SDNP or planning constraints.",
        }
      : {
          h: "Geographic distribution",
          p: "Each pin is a workshop, coloured by the interpretive sentiment classification assigned during extraction. Click a pin for detail.",
        };

  return (
    <div className="space-y-4" data-testid="map-tab">
      {/* Header + view switch */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900">
            {titleCopy.h}
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">{titleCopy.p}</p>
        </div>
        <div
          className="inline-flex border border-gray-300 bg-white text-xs"
          role="tablist"
          data-testid="map-view-switch"
        >
          <button
            type="button"
            role="tab"
            aria-selected={view === VIEW_INTERPRETIVE}
            onClick={() => setView(VIEW_INTERPRETIVE)}
            data-testid="map-view-interpretive"
            className={
              "px-3 py-2 transition-colors " +
              (view === VIEW_INTERPRETIVE
                ? "bg-[#2F4F4F] text-white"
                : "text-gray-700 hover:bg-gray-50")
            }
          >
            Interpretive sentiment
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === VIEW_SUPPORT}
            onClick={() => setView(VIEW_SUPPORT)}
            data-testid="map-view-support"
            className={
              "px-3 py-2 border-l border-gray-300 transition-colors " +
              (view === VIEW_SUPPORT
                ? "bg-[#2F4F4F] text-white"
                : "text-gray-700 hover:bg-gray-50")
            }
          >
            Support for onshore wind
          </button>
        </div>
      </div>

      {/* Legend (changes with view) */}
      <div className="flex items-center gap-3 text-xs flex-wrap" data-testid="map-legend">
        <span
          className="inline-flex items-center gap-1.5"
          data-testid="legend-sdnp"
        >
          <span
            className="inline-block h-2.5 w-5 border border-dashed border-[#2F4F4F]"
            style={{ backgroundColor: "rgba(47,79,79,0.12)" }}
          />
          <span className="text-gray-700">Approximate SDNP boundary</span>
        </span>

        {view === VIEW_SUPPORT ? (
          <>
            {["strong", "broad", "against"].map((k) => (
              <span key={k} className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: SUPPORT_COLORS[k] }}
                />
                <span className="text-gray-700">{SUPPORT_LABELS[k]}</span>
              </span>
            ))}
          </>
        ) : (
          Object.entries(SENTIMENT_COLORS)
            .filter(([k]) => k !== "unknown")
            .map(([k, c]) => (
              <span key={k} className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: c }}
                />
                <span className="text-gray-700">{SENTIMENT_LABELS[k]}</span>
              </span>
            ))
        )}
      </div>

      {/* Map */}
      <div
        className="h-[600px] w-full border border-gray-200 bg-white overflow-hidden"
        data-testid="leaflet-map"
      >
        <MapContainer
          center={DEFAULT_CENTRE}
          zoom={DEFAULT_ZOOM}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Polygon
            positions={SDNP_APPROX_POLYGON}
            pathOptions={{
              color: "#2F4F4F",
              weight: 1.5,
              dashArray: "4 4",
              fillColor: "#2F4F4F",
              fillOpacity: 0.08,
            }}
          />
          {pinned.map((w) => {
            const { color, label } = pinAppearance(w, view);
            return (
              <CircleMarker
                key={`${view}-${w.id}`}
                center={[w.lat, w.lon]}
                radius={9}
                pathOptions={{
                  color: "#ffffff",
                  weight: 2,
                  fillColor: color,
                  fillOpacity: 0.9,
                }}
                eventHandlers={{ click: () => onSelect(w.id) }}
              >
                <Popup>
                  <div className="space-y-1">
                    <div className="font-semibold text-sm">{w.workshop_name}</div>
                    <div className="text-xs text-gray-600">
                      {w.parish_or_town}
                      {w.county ? ` · ${w.county}` : ""}
                    </div>
                    <div className="text-xs">
                      <span
                        className="inline-block h-2 w-2 rounded-full mr-1.5 align-middle"
                        style={{ backgroundColor: color }}
                      />
                      {label}
                    </div>
                    <button
                      className="text-xs text-[#2F4F4F] underline mt-1"
                      onClick={() => onSelect(w.id)}
                      data-testid={`map-popup-detail-${w.id}`}
                    >
                      Open workshop detail →
                    </button>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>

      {missing > 0 && (
        <p className="text-xs text-gray-500" data-testid="map-missing-notice">
          {missing} workshop{missing === 1 ? "" : "s"} could not be placed on
          the map (no known coordinates for the extracted location). These
          remain available in the Workshop Explorer table.
        </p>
      )}

      {view === VIEW_SUPPORT ? (
        <p
          className="text-xs text-gray-500"
          data-testid="map-support-note"
        >
          This view collapses the dual-layer classification into a four-band
          signal of support for onshore wind itself. <strong>Strongly in favour</strong>{" "}
          = workshops that actively designed deployments (turbine numbers,
          siting, configurations). <strong>Broadly supportive</strong> = engaged
          constructively with the discussion. <strong>Effectively ruled out</strong>{" "}
          = supportive in principle but did not engage in any concrete design
          discussion. <strong>Against</strong> = actually opposed to the
          technology. Workshops in the amber band are not the same as workshops
          in the red band — they have not rejected wind, only declined to
          discuss it in detail.
        </p>
      ) : null}

      <p className="text-xs text-gray-500" data-testid="map-sdnp-note">
        The shaded area is an <em>approximate</em> trace of the South Downs
        National Park for orientation only. It is not an authoritative boundary
        and should not be used for planning purposes.
      </p>
    </div>
  );
}
